import { trackSmartflowStarted, trackStepCompleted } from '@/lib/analytics';
import { SignatoryEntry } from '@/modules/templates/lib/signatory-config';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Check, Cloud, CloudUpload, Loader2, Menu } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui-kit/sheet';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getScreenIcon } from '@/modules/templates/lib/dynamic-flow/utils';
import ApplyStandardsBanner from './ApplyStandardsBanner';
import { ScreenWithFields, useDynamicForm } from './DynamicFormContext';
import DynamicScreenContent from './DynamicScreenContent';
import DynamicField, { FieldConfig } from './field-renderers';
import HeaderWithStepper from './HeaderWithStepper';
import { useAiEnrichment } from './hooks/useAiEnrichment';
import { useApplyStandards } from './hooks/useApplyStandards';
import { useDynamicFields } from './hooks/useDynamicFields';
import { useAutoSaveDraft, type SaveStatus } from './hooks/useAutoSaveDraft';
import { useGenerationFlow } from './hooks/useGenerationFlow';
import LoadingScreen from './LoadingScreen';
import NavigationButtons from './NavigationButtons';
import ProgressBar from './ProgressBar';
import ScreenHeaderWithIcon from './ScreenHeaderWithIcon';
import SignatoryScreenRenderer from './SignatoryRenderer';
import ValidationErrorBanner from './ValidationErrorBanner';
import WelcomeScreen from './WelcomeScreen';

function SaveStatusIndicator({
  status,
  alwaysVisible = false,
}: {
  status: SaveStatus;
  /** When true, an `idle` status renders a neutral "Auto-save" badge
      instead of returning null. Used by the mobile top bar so the user
      always sees a status pill. */
  alwaysVisible?: boolean;
}) {
  if (status === 'idle' && !alwaysVisible) return null;

  const config = {
    idle: {
      icon: <Cloud className="size-3.5" />,
      text: 'Auto-save',
      className: 'text-muted-foreground',
    },
    saving: {
      icon: <CloudUpload className="size-3.5 animate-pulse" />,
      text: 'Saving...',
      className: 'text-muted-foreground',
    },
    saved: { icon: <Check className="size-3.5" />, text: 'Saved', className: 'text-emerald-600' },
    error: {
      icon: <AlertCircle className="size-3.5" />,
      text: 'Save failed',
      className: 'text-destructive',
    },
  }[status];

  if (!config) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`flex items-center gap-1.5 text-xs font-medium ${config.className}`}
    >
      {config.icon}
      <span>{config.text}</span>
    </motion.div>
  );
}

const DynamicSmartFlowContent = () => {
  const { t } = useTranslation();

  const [showWelcome, setShowWelcome] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileStatus, setTurnstileStatus] = useState<
    'success' | 'error' | 'expired' | 'required'
  >('required');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [tosTransitioning, setTosTransitioning] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [mobileStepsOpen, setMobileStepsOpen] = useState(false);

  const {
    config,
    formData,
    setFieldValue: originalSetFieldValue,
    errors,
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    canProceed,
    enrichmentContext,
    visibleScreens,
    getVisibleFields,
    setFormDataBulk,
    setEnrichmentContextBulk,
    setCurrentStepDirect,
  } = useDynamicForm();

  // Wrap setFieldValue to clear validation errors when user fills fields
  const setFieldValue = useCallback(
    (name: string, value: unknown) => {
      originalSetFieldValue(name, value);
      setShowValidationErrors(false);
    },
    [originalSetFieldValue]
  );

  // --- Hooks ---
  const { aiEnrichmentState, runAiEnrichmentInBackground } = useAiEnrichment();

  const {
    dynamicFieldsCache,
    setDynamicFieldsCache,
    dynamicFieldsLoading,
    dynamicFieldsError,
    setDynamicFieldsError,
    dynamicFieldsFetchedRef,
    jurisdictionCache,
    prefetchingStepIndices,
  } = useDynamicFields();

  const {
    applyingStandard,
    appliedStandard,
    applyStandardsRecursively,
    applyDynamicStandardsRecursively,
  } = useApplyStandards(setFieldValue);

  const autoSave = useAutoSaveDraft({
    formData,
    enrichmentContext,
    dynamicFieldsCache,
    currentStep,
    totalSteps,
    config,
  });

  const generation = useGenerationFlow(turnstileToken, autoSave.markAsCompleted, autoSave.draftId);

  // Hydrate form state when a draft is loaded
  const draftHydratedRef = useRef(false);
  useEffect(() => {
    if (autoSave.draftLoaded && autoSave.loadedDraft && !draftHydratedRef.current) {
      draftHydratedRef.current = true;
      const draft = autoSave.loadedDraft;

      try {
        if (draft.FormData) setFormDataBulk(JSON.parse(draft.FormData));
        if (draft.EnrichmentContext) setEnrichmentContextBulk(JSON.parse(draft.EnrichmentContext));
        if (draft.DynamicFields) {
          const parsed = JSON.parse(draft.DynamicFields);
          setDynamicFieldsCache(parsed);
        }
        if (typeof draft.CurrentStep === 'number') setCurrentStepDirect(draft.CurrentStep);
      } catch (err) {
        console.warn('Failed to hydrate draft:', err);
      }
    }
  }, [
    autoSave.draftLoaded,
    autoSave.loadedDraft,
    setFormDataBulk,
    setEnrichmentContextBulk,
    setCurrentStepDirect,
    setDynamicFieldsCache,
  ]);

  // --- Effects ---

  // Track smartflow started when component mounts with config
  useEffect(() => {
    if (config) {
      trackSmartflowStarted(config.slug, config.title);
    }
  }, [config]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showWelcome && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        if (turnstileStatus === 'success' && turnstileToken) {
          setShowWelcome(false);
        }
        return;
      }

      if (!showWelcome && !generation.isGenerating && e.key === 'Enter') {
        const activeElement = document.activeElement;
        if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
          return;
        }

        if (currentStep < totalSteps - 1) {
          e.preventDefault();
          const screen = config?.screens[currentStep];
          runAiEnrichmentInBackground(screen);
          nextStep();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    showWelcome,
    turnstileStatus,
    turnstileToken,
    generation.isGenerating,
    currentStep,
    totalSteps,
    config,
    runAiEnrichmentInBackground,
    nextStep,
  ]);

  // --- Derived state ---
  const stepDefinitions = useMemo(() => {
    if (!visibleScreens || !Array.isArray(visibleScreens) || visibleScreens.length === 0) {
      return [];
    }
    return visibleScreens.map((screen, index) => ({
      id: screen.id || `step-${index}`,
      title:
        screen.title ||
        t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_STEPNUMBER', {
          number: index + 1,
        }),
    }));
  }, [visibleScreens, t]);

  // --- Handlers ---
  const handleContinue = () => {
    if (canProceed()) {
      const screen = visibleScreens[currentStep];

      if (config && screen) {
        trackStepCompleted(
          currentStep + 1,
          screen.id || screen.title || `step_${currentStep + 1}`,
          config.slug
        );
      }

      runAiEnrichmentInBackground(screen);
      setShowValidationErrors(false);
      nextStep();
    } else {
      setShowValidationErrors(true);

      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-field-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  };

  const handleDynamicRetry = () => {
    if (!currentScreen) return;
    dynamicFieldsFetchedRef.current.delete(currentScreen.id);
    setDynamicFieldsError(null);
    setDynamicFieldsCache((prev) => {
      const newCache = { ...prev };
      delete newCache[currentScreen.id];
      return newCache;
    });
  };

  // --- Render ---

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-selise-blue" />
      </div>
    );
  }

  const currentScreen = visibleScreens[currentStep];

  // Loading Screen - shows during document generation
  if (generation.isGenerating || generation.generationError || generation.isTokenExpired) {
    return (
      <LoadingScreen
        currentStage={generation.currentStage}
        isTokenExpired={generation.isTokenExpired}
        setRetryTurnstileStatus={generation.setRetryTurnstileStatus}
        setRetryTurnstileToken={generation.setRetryTurnstileToken}
        retryGeneration={generation.retryGeneration}
        generationError={generation.generationError}
        setGenerationError={generation.setGenerationError}
        handleRetryGeneration={generation.handleRetryGeneration}
        handleSubmit={generation.handleSubmit}
        displayedProgress={generation.displayedProgress}
        generationStepIndex={generation.generationStepIndex}
        retryTurnstileStatus={generation.retryTurnstileStatus}
        retryTurnstileToken={generation.retryTurnstileToken}
        generationSteps={generation.generationSteps}
        currentTipIndex={generation.currentTipIndex}
        generationTips={generation.generationTips}
        fakeProgress={generation.fakeProgress}
      />
    );
  }

  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Welcome Screen
  if (showWelcome) {
    return (
      <WelcomeScreen
        config={config}
        turnstileStatus={turnstileStatus}
        setTurnstileStatus={setTurnstileStatus}
        setTurnstileToken={setTurnstileToken}
        tosAccepted={tosAccepted}
        tosTransitioning={tosTransitioning}
        setTosTransitioning={setTosTransitioning}
        turnstileToken={turnstileToken}
        setTosAccepted={setTosAccepted}
        setShowWelcome={setShowWelcome}
      />
    );
  }

  // Main Form Flow
  return (
    <div className="min-h-dvh bg-[hsl(var(--bg))] flex flex-col">
      {/* Mobile top bar — hamburger opens the Steps drawer, save status
          sits on the right. md:hidden so desktop renders unchanged. */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-2 px-3 h-14 border-b border-border bg-[hsl(var(--bg))]/95 backdrop-blur-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileStepsOpen(true)}
          aria-label="Open steps"
          className="h-9 w-9"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <AnimatePresence mode="wait">
          <SaveStatusIndicator
            key={autoSave.saveStatus}
            status={autoSave.saveStatus}
            alwaysVisible
          />
        </AnimatePresence>
      </div>

      {/* Mobile steps drawer — Sheet on the left, contains the same
          HeaderWithStepper used by the desktop sidebar. Tapping a step
          closes the drawer (via wrapped goToStep). */}
      <Sheet open={mobileStepsOpen} onOpenChange={setMobileStepsOpen}>
        <SheetContent
          side="left"
          className="w-[85vw] max-w-sm p-0 overflow-y-auto md:hidden z-9999"
        >
          <SheetTitle className="sr-only">Steps</SheetTitle>

          {/* Template header — icon tile + title + slug at the top of the
              drawer so users know which flow they're in. Uses getScreenIcon
              to derive an icon from the template title, same heuristic
              the screen header uses. */}
          {(() => {
            const TemplateIcon = getScreenIcon(config.title ?? '');
            return (
              <div className="px-4 pt-4 pb-3 border-b border-border flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-[hsl(var(--brand-primary))/0.1] flex items-center justify-center text-brand-primary">
                  <TemplateIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold text-[hsl(var(--fg))] truncate">{config.title}</h2>
                  <code className="text-xs text-globe-grey font-mono truncate block">
                    {config.slug}
                  </code>
                </div>
              </div>
            );
          })()}

          <HeaderWithStepper
            stepDefinitions={stepDefinitions}
            currentStep={currentStep}
            goToStep={(idx) => {
              goToStep(idx);
              setMobileStepsOpen(false);
            }}
            prefetchingStepIndices={prefetchingStepIndices}
            aiEnrichmentState={aiEnrichmentState}
            forceFullStepper
          />
        </SheetContent>
      </Sheet>

      {/* Desktop save-status — floats below the app navbar. Hidden on
          mobile because the mobile top bar already shows it. */}
      <div className="hidden md:block fixed top-20 right-4 z-999 pointer-events-none">
        <AnimatePresence mode="wait">
          <SaveStatusIndicator key={autoSave.saveStatus} status={autoSave.saveStatus} />
        </AnimatePresence>
      </div>

      <ProgressBar progress={progress} />

      <div className="flex flex-col md:flex-row z-10 gap-4 md:gap-8 container mx-auto">
        {/* Desktop sidebar stepper — mobile uses the Sheet above. */}
        <div className="hidden md:block">
          <HeaderWithStepper
            stepDefinitions={stepDefinitions}
            currentStep={currentStep}
            goToStep={goToStep}
            prefetchingStepIndices={prefetchingStepIndices}
            aiEnrichmentState={aiEnrichmentState}
          />
        </div>

        <div className="flex-1 flex items-center justify-center px-0 md:px-4 mt-2 md:mt-8">
          {/* Scroll viewport — leaves room at the bottom on mobile for
              the fixed nav bar (~72px); desktop keeps the previous height. */}
          <div className="w-full h-[calc(100dvh-12rem)] md:h-[calc(100vh-5rem)] overflow-y-auto px-2 pb-24 md:pb-2 pt-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Card Container */}
                <div className="bg-linear-to-br pb-4 from-background/95 to-[hsl(var(--brand-primary))/5] backdrop-blur-xl rounded-xl shadow-xl shadow-[hsl(var(--brand-primary))/5] border border-border relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[hsl(var(--brand-primary))/5] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                  <ScreenHeaderWithIcon currentScreen={currentScreen} />

                  {showValidationErrors && <ValidationErrorBanner currentScreen={currentScreen} />}

                  {/* Fields with Staggered Animation */}
                  <motion.div
                    className="space-y-4 sm:space-y-8 px-4 py-3 sm:px-5 sm:py-4 relative z-10"
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: { staggerChildren: 0.1 },
                      },
                    }}
                    initial="hidden"
                    animate="show"
                  >
                    <AnimatePresence mode="popLayout">
                      {(currentScreen as ScreenWithFields)?.type === 'dynamic' ? (
                        <DynamicScreenContent
                          currentScreen={currentScreen}
                          dynamicFieldsLoading={dynamicFieldsLoading}
                          dynamicFieldsError={dynamicFieldsError}
                          dynamicFieldsCache={dynamicFieldsCache}
                          jurisdictionName={jurisdictionCache[currentScreen?.id] || null}
                          appliedStandard={appliedStandard}
                          applyingStandard={applyingStandard}
                          onApplyDynamicStandards={applyDynamicStandardsRecursively}
                          setFieldValue={setFieldValue}
                          onRetry={handleDynamicRetry}
                          onSkip={nextStep}
                        />
                      ) : (currentScreen as ScreenWithFields)?.type === 'signatory' ? (
                        <SignatoryScreenRenderer
                          configJson={(currentScreen as ScreenWithFields).signatoryConfig}
                          value={(formData.signatories as SignatoryEntry[]) || []}
                          onChange={(signatories) => setFieldValue('signatories', signatories)}
                          errors={errors}
                          formData={formData}
                        />
                      ) : (
                        /* Standard Screen */
                        <div>
                          <ApplyStandardsBanner
                            variant="standard"
                            currentScreen={currentScreen}
                            appliedStandard={appliedStandard}
                            applyingStandard={applyingStandard}
                            onApply={() => applyStandardsRecursively(currentScreen)}
                          />

                          <AnimatePresence mode="popLayout">
                            {currentScreen &&
                              getVisibleFields(currentScreen).map((field) => (
                                <motion.div
                                  key={field.id}
                                  variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    show: { opacity: 1, y: 0 },
                                  }}
                                  className="my-3"
                                  initial="hidden"
                                  animate="show"
                                  exit={{ opacity: 0, scale: 0.95 }}
                                  layout
                                  data-field-error={errors[field.name] ? 'true' : undefined}
                                >
                                  <DynamicField
                                    field={field as FieldConfig}
                                    value={formData[field.name]}
                                    onChange={setFieldValue}
                                    error={errors[field.name]}
                                    enrichmentContext={enrichmentContext}
                                    formData={formData}
                                  />
                                </motion.div>
                              ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>

                {/* Desktop inline NavigationButtons — mobile uses the
                    fixed bottom bar at the end of this component. The
                    pt-6 keeps the original spacing under the card on
                    desktop (the component itself no longer adds it). */}
                <div className="hidden md:block pt-6">
                  <NavigationButtons
                    currentScreen={currentScreen}
                    dynamicFieldsLoading={dynamicFieldsLoading}
                    dynamicFieldsCache={dynamicFieldsCache}
                    prefetchingStepIndices={prefetchingStepIndices}
                    turnstileToken={turnstileToken}
                    turnstileStatus={turnstileStatus}
                    onContinue={handleContinue}
                    onSubmit={generation.handleSubmit}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile fixed bottom nav bar — Continue / Submit / Back live
          here so the form fields scroll under them. Padded for safe
          area / thumb reach. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-[hsl(var(--bg))]/95 backdrop-blur-sm px-3 pt-2 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        <NavigationButtons
          currentScreen={currentScreen}
          dynamicFieldsLoading={dynamicFieldsLoading}
          dynamicFieldsCache={dynamicFieldsCache}
          prefetchingStepIndices={prefetchingStepIndices}
          turnstileToken={turnstileToken}
          turnstileStatus={turnstileStatus}
          onContinue={handleContinue}
          onSubmit={generation.handleSubmit}
        />
      </div>

      <div className="hidden md:block pb-6" />
    </div>
  );
};

export default DynamicSmartFlowContent;
